import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mic, ArrowRight, CheckCircle2, Globe, Zap, ShieldCheck, Sparkles, Camera, Type,
  Droplet, Hammer, Wind, Paintbrush, Leaf, Home, Ruler, Fence, Star, Quote as QuoteIcon,
} from "lucide-react";
import { RivetWordmark, RivetMark } from "@/components/RivetLogo";
import { Button } from "@/components/ui/button";
import { IDS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { templates as tplApi } from "@/lib/api";

const HAPPY_IMG =
  "https://images.unsplash.com/photo-1676630656246-3047520adfdf?fm=jpg&q=75&w=1600&auto=format&fit=crop";

const TRADE_ICONS = {
  zap: Zap, droplet: Droplet, hammer: Hammer, wind: Wind, paintbrush: Paintbrush,
  leaf: Leaf, home: Home, sparkles: Sparkles, ruler: Ruler, fence: Fence,
};

const REVIEWS = [
  {
    name: "Marcus D.",
    trade: "Electrician",
    location: "Melbourne, AU",
    initials: "MD",
    accent: "#2563EB",
    rating: 5,
    quote: "I quoted three jobs in the truck between callouts today. That's an extra $2k of work I'd normally have written up tomorrow — and probably lost half of.",
  },
  {
    name: "Sarah W.",
    trade: "Painter",
    location: "Auckland, NZ",
    initials: "SW",
    accent: "#10B981",
    rating: 5,
    quote: "The voice thing is unreal. I ramble about the rooms, wall areas, coats — RivetQuote spits back a proper line-itemised quote in under a minute.",
  },
  {
    name: "Jack O.",
    trade: "Plumber",
    location: "Manchester, UK",
    initials: "JO",
    accent: "#3BB2F6",
    rating: 5,
    quote: "Customers actually respond now. The public link is dead simple — Accept, changes, or decline. My win rate is up because they don't sit on my quote for a week.",
  },
  {
    name: "Diego R.",
    trade: "Carpenter",
    location: "Austin, TX",
    initials: "DR",
    accent: "#F59E0B",
    rating: 5,
    quote: "The modification workflow is money. Customer asks for a change, I get pinged, I make a v2 in the app, they see the updated numbers same day. No email chains.",
  },
  {
    name: "Priya K.",
    trade: "HVAC",
    location: "Bengaluru, IN",
    initials: "PK",
    accent: "#EA580C",
    rating: 5,
    quote: "I set my currency to INR and customers see totals in their own — makes a difference for cross-city work. Templates saved me a full evening of setup.",
  },
  {
    name: "Tom B.",
    trade: "Fencer",
    location: "Perth, AU",
    initials: "TB",
    accent: "#001F3F",
    rating: 5,
    quote: "Went from Word docs and PDFs to sending a link before I leave the driveway. Feels like I finally look like the professional business I actually am.",
  },
];

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={14}
            className={i < n ? "fill-[#F59E0B] text-[#F59E0B]" : "text-slate-200"} />
    ))}
  </div>
);

/** Voice-to-quote hero mock (matches the customer's screenshot). */
function VoiceQuoteMock() {
  return (
    <div className="relative">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
        {/* Card header */}
        <div className="px-6 pt-5 pb-3 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-[#2563EB]">
              New quote
            </div>
            <div className="mt-1 text-lg font-bold text-[#001F3F]"
                 style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
              What are you quoting today?
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[#64748B] bg-slate-100 px-2 py-1 rounded">
            Draft
          </div>
        </div>

        {/* Mic panel */}
        <div className="mx-6 mb-4 rounded-xl bg-[#EFF6FF] border border-[#DBEAFE] p-8 flex flex-col items-center">
          <div className="relative h-20 w-20 rounded-full bg-[#2563EB] text-white flex items-center justify-center shadow-lg rq-pulse">
            <Mic size={32} />
          </div>
          <div className="mt-4 text-sm font-semibold text-[#001F3F]">Tap to speak</div>
          <div className="mt-1 text-xs text-[#64748B]">Describe the job naturally.</div>
        </div>

        {/* Input toggle buttons */}
        <div className="px-6 pb-4 grid grid-cols-3 gap-2">
          <button className="rounded-lg border border-slate-200 py-2.5 text-xs font-semibold text-[#001F3F] hover:border-[#2563EB] hover:text-[#2563EB] transition-colors flex items-center justify-center gap-1.5">
            <Mic size={13} /> Speak
          </button>
          <button className="rounded-lg border border-slate-200 py-2.5 text-xs font-semibold text-[#001F3F] hover:border-[#2563EB] hover:text-[#2563EB] transition-colors flex items-center justify-center gap-1.5">
            <Type size={13} /> Type
          </button>
          <button className="rounded-lg border border-slate-200 py-2.5 text-xs font-semibold text-[#001F3F] hover:border-[#2563EB] hover:text-[#2563EB] transition-colors flex items-center justify-center gap-1.5">
            <Camera size={13} /> Photos
          </button>
        </div>

        {/* Result preview */}
        <div className="mx-6 mb-6 rounded-lg border border-slate-200 p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-[#001F3F]">Fence replacement</div>
            <div className="text-xs text-[#64748B] mt-0.5">Structured estimate ready to review</div>
          </div>
          <CheckCircle2 className="text-[#10B981]" size={20} />
        </div>
      </div>

      {/* Floating tag */}
      <div className="absolute -bottom-6 -left-4 sm:-left-8 rounded-lg bg-white border border-slate-200 shadow-md p-3 max-w-[240px]">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[#2563EB]">
          Review before sending
        </div>
        <div className="mt-1 text-xs text-[#334155] leading-relaxed">
          Every estimate remains editable.
        </div>
      </div>
    </div>
  );
}

const StepCard = ({ n, title, body }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#2563EB]">Step {n}</div>
    <h3 className="mt-3 text-xl font-bold tracking-tight text-[#001F3F]"
        style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>{title}</h3>
    <p className="mt-2 text-sm text-[#64748B] leading-relaxed">{body}</p>
  </div>
);

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [tpls, setTpls] = useState([]);

  useEffect(() => {
    tplApi.list().then(({ data }) => setTpls((data || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid={IDS.landing.root} className="min-h-screen bg-[#F1F5F9] text-[#001F3F]">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <Link to="/"><RivetWordmark size={34} /></Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Button onClick={() => nav("/dashboard")}
                      className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                Open Dashboard <ArrowRight size={16} className="ml-1" />
              </Button>
            ) : (
              <>
                <Button
                  data-testid={IDS.landing.ctaLogin}
                  onClick={() => nav("/login")}
                  variant="ghost"
                  className="text-[#001F3F] hover:bg-slate-100 hover:text-[#001F3F]"
                >
                  Sign in
                </Button>
                <Button
                  data-testid={IDS.landing.ctaSignup}
                  onClick={() => nav("/login?mode=signup")}
                  className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                >
                  Get started <ArrowRight size={16} className="ml-1" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 bg-[#EFF6FF] border border-[#DBEAFE] rounded-full px-3 py-1.5">
              <Sparkles size={14} className="text-[#2563EB]" />
              <span className="text-xs font-semibold text-[#2563EB]">Speak. Snap. Quote.</span>
            </div>
            <h1
              data-testid={IDS.landing.heroTitle}
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#001F3F]"
              style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}
            >
              Create<br />
              professional<br />
              quotes<br />
              <span className="text-[#2563EB]">in seconds.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#334155] leading-relaxed max-w-xl">
              Speak the job. Let AI build the quote. Send it before you leave the driveway — with every estimate editable and under your control.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => nav("/login?mode=signup")}
                className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-12 px-6 text-base font-semibold"
              >
                Create Your First Quote — Free <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button
                onClick={() => {
                  const el = document.getElementById("how-it-works");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="rounded-full border-slate-300 h-12 px-6 text-base text-[#001F3F] hover:bg-white"
              >
                See how it works
              </Button>
            </div>
            <div className="mt-4 text-xs text-[#64748B]">
              No credit card. Start with the job, not a lengthy business setup.
            </div>
          </div>
          <div className="lg:col-span-6">
            <VoiceQuoteMock />
          </div>
        </div>
      </section>

      {/* Feature bento */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          <div data-testid={IDS.landing.featureVoice}
               className="rounded-lg border border-slate-200 bg-white p-8 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
            <Mic className="text-[#2563EB]" size={28} />
            <h3 className="mt-4 text-xl font-bold tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>Voice → Quote</h3>
            <p className="mt-2 text-sm text-[#64748B] leading-relaxed">
              Record on-site, on your way home, or between jobs. Our AI extracts line items, quantities, and prices for you.
            </p>
          </div>
          <div data-testid={IDS.landing.featureFlow}
               className="rounded-lg border border-slate-200 bg-white p-8 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
            <Zap className="text-[#2563EB]" size={28} />
            <h3 className="mt-4 text-xl font-bold tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>Accept · Modify · Decline</h3>
            <p className="mt-2 text-sm text-[#64748B] leading-relaxed">
              Send a secure public link. Customers tap Accept, request changes, or decline — you get an instant notification.
            </p>
          </div>
          <div data-testid={IDS.landing.featureCurrency}
               className="rounded-lg border border-slate-200 bg-white p-8 hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
            <Globe className="text-[#2563EB]" size={28} />
            <h3 className="mt-4 text-xl font-bold tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>Global by default</h3>
            <p className="mt-2 text-sm text-[#64748B] leading-relaxed">
              Set your primary currency. Customers view totals in their local currency with live FX conversion.
            </p>
          </div>
        </div>
      </section>

      {/* Trade Templates */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">
            Trade templates
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            A flexible starting point for the work you quote.
          </h2>
          <p className="mt-3 text-sm text-[#64748B]">
            Use a relevant workflow as a starting point, then change every line item and detail to match the actual job.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tpls.map((t) => {
            const Icon = TRADE_ICONS[t.icon] || Hammer;
            return (
              <div
                key={t.id}
                data-testid={`landing-template-${t.id}`}
                className="rounded-lg border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-md bg-[#EFF6FF] text-[#2563EB] flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-[#64748B]">
                    {t.trade}
                  </div>
                </div>
                <h3 className="mt-4 text-lg font-bold tracking-tight text-[#001F3F]"
                    style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
                  {t.name}
                </h3>
                <p className="mt-1.5 text-sm text-[#64748B] leading-relaxed">{t.tagline}</p>
                <button
                  onClick={() => nav(user ? "/templates" : "/login?mode=signup")}
                  className="mt-4 text-xs uppercase tracking-widest font-semibold text-[#2563EB] hover:underline flex items-center gap-1"
                >
                  Editable quote workflow <ArrowRight size={12} />
                </button>
              </div>
            );
          })}
          <div className="rounded-lg border border-dashed border-slate-300 bg-white/60 p-6 flex flex-col justify-center">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-slate-100 text-[#64748B] flex items-center justify-center">
                <Camera size={18} />
              </div>
              <div className="text-xs uppercase tracking-widest font-semibold text-[#64748B]">
                More trades, your way
              </div>
            </div>
            <p className="mt-4 text-sm text-[#64748B] leading-relaxed">
              For fencing, roofing, cleaning, carpentry and other field-service work — start with the same flexible quote flow and make it fit the job.
            </p>
            <button
              onClick={() => nav(user ? "/templates" : "/login?mode=signup")}
              className="mt-4 text-xs uppercase tracking-widest font-semibold text-[#2563EB] hover:underline flex items-center gap-1 self-start"
            >
              See flexible workflows <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">How it works</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
            Three taps from job to signed quote.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <StepCard n="01" title="Speak the job" body="Hit the mic and describe the work naturally — even in noisy conditions." />
          <StepCard n="02" title="Review & tweak" body="AI drafts a full quote with line items. Edit, add tax, and set terms in seconds." />
          <StepCard n="03" title="Send the link" body="Send a secure public link. Customers accept, request changes, or decline right from mobile." />
        </div>
      </section>

      {/* Reviews */}
      <section data-testid="landing-reviews" className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">
              What tradies say
            </div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
              Loved by tradies quoting on the move.
            </h2>
          </div>
          <div className="flex items-center gap-4 bg-white rounded-full border border-slate-200 px-5 py-3 shadow-sm">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} className="fill-[#F59E0B] text-[#F59E0B]" />
              ))}
            </div>
            <div className="text-sm">
              <span className="font-black text-[#001F3F]">4.9</span>
              <span className="text-[#64748B]"> · early access users</span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {REVIEWS.map((r, i) => (
            <div
              key={r.name}
              data-testid={`review-card-${i}`}
              className="relative rounded-lg border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-md transition-transform duration-200"
            >
              <QuoteIcon size={20} className="text-[#DBEAFE] absolute top-4 right-4" />
              <Stars n={r.rating} />
              <p className="mt-4 text-sm text-[#334155] leading-relaxed">
                &ldquo;{r.quote}&rdquo;
              </p>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
                  style={{ backgroundColor: r.accent }}
                >
                  {r.initials}
                </div>
                <div>
                  <div className="text-sm font-bold text-[#001F3F]">{r.name}</div>
                  <div className="text-xs text-[#64748B]">{r.trade} · {r.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center text-xs text-[#64748B]">
          Early-access reviews from tradies piloting RivetQuote in Australia, New Zealand, UK, US and India.
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[#001F3F] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
              Win the jobs on your phone.
            </h2>
            <p className="mt-4 text-base text-slate-300 max-w-lg leading-relaxed">
              Every second between a customer asking and getting a quote is a chance for them to call someone else. Close the gap.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => nav("/login?mode=signup")}
                      className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-12 px-6">
                Create free account <ArrowRight size={16} className="ml-1" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <ShieldCheck size={16} className="text-[#3BB2F6]" /> Bank-grade encryption
              </div>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden border border-slate-800">
            <img src={HAPPY_IMG} alt="Handyman doing a household repair" className="w-full h-[320px] object-cover" />
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-5 sm:px-8 py-10 flex items-center justify-between text-sm text-[#64748B]">
        <RivetMark size={28} />
        <div>© {new Date().getFullYear()} RivetQuote · www.rivetquote.com</div>
      </footer>
    </div>
  );
}
